import React, { useContext, useMemo } from 'react';

import { getChartPalette } from 'components/V5Gadget/chartPalette';
import { AccountChartPalettesContext } from 'contextProviders/AccountChartPalettesProvider';
import { ChartColors } from 'theme/tokens';

interface ChartPaletteContextType {
  // The colours a chart draws with, as hex values
  colors: string[];
  // Whether the chart chose them (else the default chart colours)
  isCustom: boolean;
}

export const ChartPaletteContext = React.createContext<ChartPaletteContextType>(
  { colors: ChartColors, isCustom: false },
);

// The colours a V5 chart definition asks for (see chartPalette), including
// the account's own palettes, so editing one recolours its charts
export const ChartPaletteProvider = ({
  chartDefinition,
  children,
}: {
  chartDefinition: V5ChartDefinition;
  children: JSX.Element;
}) => {
  const { palettes: accountPalettes } = useContext(AccountChartPalettesContext);
  const palette = useMemo(
    () => getChartPalette({ chartDefinition, accountPalettes }),
    [accountPalettes, chartDefinition],
  );
  const value = useMemo(
    () =>
      palette
        ? { colors: palette, isCustom: true }
        : { colors: ChartColors, isCustom: false },
    // Keyed on the colours, not the definition object
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [palette ? palette.join(',') : ''],
  );
  return (
    <ChartPaletteContext.Provider value={value}>
      {children}
    </ChartPaletteContext.Provider>
  );
};

export const useChartPalette = () => useContext(ChartPaletteContext);
